import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Manufacturer } from 'src/@omnial/_models/catalog/manufacturer.model';
import { ProductDetail } from 'src/@omnial/_models/catalog/product.model';
import { DealerLocatorSettings } from 'src/@omnial/_models/dealer-locator/dealer-locator.settings.model';
import { Dealer } from 'src/@omnial/_models/dealer-locator/dealer.model';
import { Country } from 'src/@omnial/_models/order/country.model';
import { ManufacturerService } from 'src/@omnial/_services/catalog/manufacturer.service';
import { DealerService } from 'src/@omnial/_services/dealers/dealer.service';
import { CheckoutService } from 'src/@omnial/_services/order/checkout.service';
import { environment } from 'src/environments/environment';
import { DealerLocatorMapComponent } from './dealer-locator-map/dealer-locator-map.component';

@Component({
    selector: 'app-dealer-locator',
    templateUrl: './../../../app/shared/dealer-locator/dealer-locator.component.html',
    styleUrls: ['./../../../app/shared/dealer-locator/dealer-locator.component.scss'],
    standalone: false
})

export class DealerLocatorComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() dealerSelected = new EventEmitter<Dealer>();
  @Input() product: ProductDetail;
  @Input() lookupType: 'manufacturer' | 'specificationOption' = 'manufacturer';
  @Input() specificationOptionName: string;
  @Input() manufacturer: Manufacturer;
  @Input() dealers: Dealer[];
  @Input() allSelection: boolean = true;
  @Input() loadAll: boolean = false;
  @Input() manufacturerPreSelect: boolean = false;
  @ViewChildren('dealerLocatorMap') dealerLocatorMap: QueryList<DealerLocatorMapComponent>;
  public allDealers: Dealer[];
  public manufacturers: Manufacturer[] = [];
  public settings: DealerLocatorSettings;
  public countries: Country[];
  public preRender = false;

  public manufacturerFilter: Manufacturer[] = [];
  public selectedManufacturer: Manufacturer = null;
  public showManufacturerFilter = false;
  public subscriptions: Subscription[] = [];

  public manufacturerForm = new FormGroup({ manufacturerId: new FormControl(0) });

  constructor(private dealerService: DealerService,
    public manufacturerService: ManufacturerService,
    public checkoutService: CheckoutService) { }

  ngOnDestroy(): void {
    if (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => { sub.unsubscribe(); });
    }
  }

  ngAfterViewInit(): void {
    if (this.manufacturerForm?.controls?.manufacturerId?.value) {
      this.changeManufacturer(this.manufacturerForm?.controls?.manufacturerId?.value);
    }
  }

  ngOnInit() {
    if (environment.versionCheckUrl.indexOf("://pre.") !== -1) {
      this.preRender = true;
    }
    this.init();
  }

  public init(product?: ProductDetail) {
    if (product) {
      this.product = product;
    }
    if (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => { sub.unsubscribe(); });
    }
    this.subscriptions.push(this.dealerService.settings.subscribe(
      settings => {
        if (settings) {
          this.settings = settings;
          this.initLocator();
        } else {
          this.dealerService.loadSettings();
        }
      }));
    if (!this.manufacturer) {
      this.subscriptions.push(this.manufacturerService.manufacturers.subscribe(manufacturers => {
        if (manufacturers) {
          this.manufacturers = manufacturers;
          this.initLocator();
        } else {
          this.manufacturerService.load();
        }
      }));
    } else {
      this.manufacturers = [this.manufacturer];
    }
    if (!this.dealers) {
      this.subscriptions.push(this.dealerService.dealers.subscribe(
        dealers => {
          if (dealers) {
            this.allDealers = dealers.filter(d => d.isVisible);
            if (!this.allDealers) {
              this.allDealers = [];
            }
            this.initLocator();
          } else {
            if (this.loadAll) {
              this.dealerService.loadAll();
            } else {
              this.dealerService.load();
            }
          }
        }));
    } else {
      this.allDealers = this.dealers;
    }
    this.subscriptions.push(this.checkoutService.getAllowableCountries(true).subscribe(
      resShip => {
        this.countries = resShip as Country[];
        this.initLocator();
      }));
  }

  public initLocator(): void {
    if (this.preRender || !this.settings || !this.allDealers || !this.countries || !this.manufacturers) {
      return;
    }
    if (this.product && this.settings.linkToManufacturers && this.lookupType == 'manufacturer') {
      const filteredDealers: Dealer[] = [];
      this.selectedManufacturer = this.manufacturers.find(m => m.id === this.product.manufacturer?.id)
      this.allDealers.forEach((dealer) => {
        if (dealer.manufacturerIds.some(m => m === this.product?.manufacturer?.id)) {
          filteredDealers.push(dealer);
        };
      });
      this.initMap(filteredDealers);
    } else if (this.product && this.lookupType == 'specificationOption') {
      const filteredDealers: Dealer[] = [];
      const optionIds: number[] = [];
      this.product.specifications?.forEach((specification) => {
        if (!this.specificationOptionName) {
          optionIds.push(specification.id);
        } else if (this.specificationOptionName == specification.specificationAttributeName) {
          optionIds.push(specification.id);
        }
      });
      this.allDealers.forEach((dealer) => {
        dealer.specificationOptionIds.forEach((spec) => {
          if (optionIds.find(s => s == spec)) {
            filteredDealers.push(dealer);
          }
        });
      });
      this.initMap(filteredDealers);
    } else if (!this.allSelection) {
      this.manufacturerFilter = this.getManufacturerFilter();
      const filteredDealers: Dealer[] = [];
      if (this.manufacturerFilter && this.manufacturerFilter.length > 0) {
        this.selectedManufacturer = this.manufacturers.find(m => m.id === this.manufacturerFilter[0].id);
      }
      this.allDealers.forEach((dealer) => {
        if (dealer.manufacturerIds.some(m => m === this.selectedManufacturer?.id)) {
          filteredDealers.push(dealer);
        };
      });
      if (this.manufacturerFilter?.length > 1) {
        this.showManufacturerFilter = true;
      }
      this.initMap(filteredDealers);
    } else {
      if (this.settings.linkToManufacturers) {
        this.manufacturerFilter = this.getManufacturerFilter();
        if (this.manufacturerFilter?.length > 1) {
          this.showManufacturerFilter = true;
          if (this.manufacturerPreSelect) {
            this.changeManufacturer(this.manufacturerFilter[0].id);
          } else{
            this.initMap(this.allDealers);
          }
        } else{
          this.initMap(this.allDealers);
        }
      }
    }
  }

  public getManufacturerFilter(): Manufacturer[] {
    const manufacturerFilter: Manufacturer[] = [];
    this.allDealers.forEach((dealer) => {
      dealer.manufacturerIds.forEach((manufacturerId) => {
        if (!manufacturerFilter.find(m => m.id == manufacturerId)) {
          const manufacturer = this.manufacturers.find(m => m.id === manufacturerId);
          if (manufacturer) {
            manufacturerFilter.push(manufacturer);
          }
        }
      });
    });
    return manufacturerFilter.sort(function (a, b) { return a.displayOrder - b.displayOrder });
  }

  public setDealer(dealer: Dealer) {
    this.dealerLocatorMap?.forEach((dealerLocatorMap) => {
      dealerLocatorMap.setDealer(dealer);
    })
  }

  public emitDealer(dealer: Dealer) {
    this.dealerSelected.emit(dealer);
  }

  public changeManufacturer(id?: number): void {
    // Get the Dealer Associated with the Manufacturer
    let selectedManufacturerId = id;
    if (!selectedManufacturerId) {
      selectedManufacturerId = this.manufacturerForm.controls.manufacturerId.value;
    }
    if (selectedManufacturerId && selectedManufacturerId !== 0) {
      this.selectedManufacturer = this.manufacturerFilter.find(m => m.id === selectedManufacturerId)
      const dealers: Dealer[] = [];
      this.allDealers.forEach((dealer) => {
        if (dealer.manufacturerIds.some(m => m === selectedManufacturerId)) {
          dealers.push(dealer);
        };
      });
      this.initMap(dealers);
    } else {
      this.selectedManufacturer = null;
      this.initMap(this.allDealers);
    }
  }

  public initMap(dealers: Dealer[]): void {
    this.dealerLocatorMap?.forEach((dealerLocatorMap) => {
      dealerLocatorMap.manufacturers = this.manufacturers;
      dealerLocatorMap.dealers = dealers;
      dealerLocatorMap.settings = this.settings;
      dealerLocatorMap.init();
    });
  }

  public showMap(): void {
    this.preRender = false;
    setTimeout(() => { this.initLocator(); }, 200);
  }
}
